import React, { useEffect, useRef } from 'react';
import _isObject from 'lodash/isObject';
import _merge from 'lodash/merge';
import { useTranslation } from 'react-i18next';
import { useCmsContext, CmsProvider } from '@/context/cms';
import {
  useStoreFinderContext,
  StoreFinderProvider,
} from '@/storeFinder/context';

// import gtm stuff
import { pushGtmServiceLocatorNearMe } from '@/utils/Controllers/GTM/Operations/ServiceLocator';

// import api
import {
  MAX_DISTANCE,
  LAT_LNG,
  DEFAULT_DISTANCE,
} from '@/storeFinder/context/selectors';
import getStores from '@/api/get/storeLocator';
import getDistance from '@/api/get/allDistance';
import getType from '@/api/get/typeOfService';

// import utils
import generateTypeOfService from '@/utils/Common/GenerateTypeOfService';

// import hooks
import useFetchCmsComponents from '@/hooks/useFetchCmsComponents';
import useFirstRenderReveal from '@/hooks/useFirstRenderReveal';
import useGoogleMap from '@/hooks/useGoogleMap';

// import components
import GoogleAutocomplete from '@/components/atoms/GoogleAutocomplete';
import ButtonTextIcon from '@/components/atoms/ButtonTextIcon';
import AccordionFilter from '@/components/organisms/AccordionFilter';
import ServiceResults from '@/components/organisms/ServiceResults';
import GoogleMap from '@/components/organisms/GoogleMap';
import useGoogleMarker from '@/hooks/useGoogleMarker';

const StoreFinderApp = () => {
  const { t } = useTranslation();
  const [
    {
      currentLatLng,
      currentDistance,
      currentService,
      pos,
      distance,
      service,
      markers,
      searched,
    },
    {
      setCurrentDistance,
      setPos,
      setDistance,
      setService,
      setMarkers,
      setSearch,
    },
  ] = useStoreFinderContext();
  const [{ componentsLoading }] = useCmsContext();
  const mapContainerRef = useRef();
  const [googleMapJSLoaded, googleMap] = useGoogleMap({
    wrapperRef: mapContainerRef,
    readyToRender: !componentsLoading,
  });
  const wrapperRef = useFirstRenderReveal(googleMapJSLoaded);
  const fetchLoading = !googleMapJSLoaded || componentsLoading;
  const isMobile = window.matchMedia('(max-width:899px)').matches;
  const anythingSearched = searched !== undefined;
  const locale = window.location.pathname.substring(4, 6);
  const defaultPos = _isObject(LAT_LNG[locale])
    ? LAT_LNG[locale]
    : {
        latitude: 0,
        longitude: 0,
      };
  const findStoreByCurrentPos = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(position => {
      pushGtmServiceLocatorNearMe();
      setSearch({
        currentLatLng: {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        },
      });
    });
  };

  // hooks
  useEffect(() => {
    if (!anythingSearched && !currentLatLng && currentDistance === null) {
      setTimeout(() => {
        setSearch({
          currentDistance: MAX_DISTANCE,
          currentLatLng: defaultPos,
        });
      }, 1000);
      return;
    }
    getStores({
      ...currentLatLng,
      distance: currentDistance,
      type: currentService && currentService.length > 0 ? currentService : null,
      successCB: data => {
        if (
          anythingSearched &&
          data.length === 0 &&
          currentDistance === DEFAULT_DISTANCE
        ) {
          setCurrentDistance(MAX_DISTANCE);
          return;
        }
        setPos(data);
      },
    });

    getDistance({
      successCB: data => setDistance(data),
    });

    getType({
      successCB: data => {
        if (currentService.length === 0) {
          setService(generateTypeOfService(data, currentService));
        }
      },
    });
  }, [currentLatLng, currentDistance, currentService, searched]);

  useFetchCmsComponents('dlgStoreFinderPage');
  useGoogleMarker({
    pos,
    markers,
    googleMap,
    setMarkers,
    googleMapJSLoaded,
    currentLatLng,
  });

  if (fetchLoading) return null;

  const features = Object.keys(_merge(pos.map(p => p.features)));

  return (
    <div className='dln-serviceLocator__wrapper' ref={wrapperRef}>
      <div className='dln-serviceLocator__aside'>
        <GoogleAutocomplete googleMapIsLoaded={googleMapJSLoaded} />
        <ButtonTextIcon
          onClick={findStoreByCurrentPos}
          text={t('storeFinder:store.finder.near.label')}
          modifiers={['nearMe', 'brand-01']}
          glyph='L'
        />
        {anythingSearched && (
          <AccordionFilter
            distances={distance}
            services={service.filter(s => features.indexOf(s.value))}
          />
        )}
        <ServiceResults markers={markers} pos={pos} hidden={isMobile} />
      </div>
      <GoogleMap containerRef={mapContainerRef} />
      <ServiceResults markers={markers} pos={pos} hidden={!isMobile} />
    </div>
  );
};

StoreFinderApp.propTypes = {};

const StoreFiderPage = () => (
  <CmsProvider>
    <StoreFinderProvider>
      <StoreFinderApp />
    </StoreFinderProvider>
  </CmsProvider>
);

export default StoreFiderPage;
